import React, { useEffect, useState } from 'react';
import { toastr } from 'react-redux-toastr';

import { formatCnpj, formatPhone } from '../garage/format';
import { cepMask, onlyNumbers, phoneMask } from '../utils/normalize';
import { SOURCE, fetchCep, missingFields, saveMyData } from './providerData';

const FIELDS = [
  { key: 'tradingName', label: 'Nome fantasia', autoComplete: 'organization' },
  { key: 'name', label: 'Razão social' },
  { key: 'email', label: 'E-mail', type: 'email', autoComplete: 'email' },
  { key: 'phone', label: 'Telefone', type: 'tel', autoComplete: 'tel', mask: phoneMask },
];

const ADDRESS = [
  { key: 'zipcode', label: 'CEP', inputMode: 'numeric', mask: cepMask, span: 1 },
  { key: 'street', label: 'Rua', span: 3 },
  { key: 'streetNumber', label: 'Número', inputMode: 'numeric', span: 1 },
  { key: 'complement', label: 'Complemento', optional: true, span: 1 },
  { key: 'neighborhood', label: 'Bairro', span: 2 },
  { key: 'city', label: 'Cidade', span: 3 },
  { key: 'state', label: 'UF', maxLength: 2, span: 1 },
];

const addressLine = (form) => {
  const first = [form.street, form.streetNumber].filter(Boolean).join(', ');
  const second = [form.neighborhood, form.city && form.state ? `${form.city}/${form.state}` : form.city].filter(Boolean).join(' · ');
  return [first, form.complement, second].filter(Boolean).join(' — ');
};

function Field({ field, value, onChange, onBlur, invalid, readOnly, hint }) {
  const display = field.mask ? field.mask(value) : value;
  return (
    <label className={`pv-field${field.span ? ` pv-span-${field.span}` : ''}`}>
      <span>{field.label}{field.optional && <em> (opcional)</em>}</span>
      <input
        type={field.type || 'text'}
        inputMode={field.inputMode}
        autoComplete={field.autoComplete}
        maxLength={field.maxLength}
        value={display}
        aria-invalid={invalid || undefined}
        onChange={(event) => onChange(field.key, event.target.value)}
        onBlur={onBlur}
        readOnly={readOnly}
      />
      {hint && <small className='pv-field-hint'>{hint}</small>}
    </label>
  );
}

// Dados da empresa: leitura compacta; "Editar" abre o formulario no mesmo lugar.
export default function CompanyDataPanel({ company, myData, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(myData.form);
  const [invalid, setInvalid] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(myData.form); }, [myData]);

  const change = (key, raw) => {
    let value = raw;
    if (key === 'phone' || key === 'zipcode' || key === 'streetNumber') value = onlyNumbers(raw);
    if (key === 'state') value = raw.toUpperCase().replace(/[^A-Z]/g, '');
    setForm((previous) => ({ ...previous, [key]: value }));
    setInvalid((list) => list.filter((item) => item !== key));
  };

  const handleCepBlur = async () => {
    try {
      const address = await fetchCep(form.zipcode);
      if (address) setForm((previous) => ({ ...previous, ...address }));
    } catch (err) {
      // Sem o ViaCEP a oficina preenche o endereco a mao.
    }
  };

  const cancel = () => {
    setForm(myData.form);
    setInvalid([]);
    setEditing(false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const missing = missingFields(form);
    if (missing.length) {
      setInvalid(missing);
      toastr.warning('Preencha os campos destacados.');
      return;
    }
    setSaving(true);
    try {
      await saveMyData(company, myData, form);
      toastr.success('Dados atualizados.');
      setEditing(false);
      onSaved();
    } catch (err) {
      const message = err && err.response && err.response.data && err.response.data.message;
      toastr.error('Não foi possível salvar os dados.', message || 'Tente de novo em instantes.');
    } finally {
      setSaving(false);
    }
  };

  const fromOsDigital = Number(company.sourceId) === SOURCE.OS_DIGITAL;

  if (!editing) {
    const rows = [
      { label: 'CNPJ', value: formatCnpj(myData.cpfCnpj) },
      { label: 'Razão social', value: form.name },
      { label: 'E-mail', value: form.email },
      { label: 'Telefone', value: formatPhone(form.phone) },
      { label: 'Endereço', value: addressLine(form) },
    ];
    return (
      <section className='pv-panel' aria-labelledby='pv-data-title'>
        <div className='pv-panel-head'>
          <h2 id='pv-data-title' className='pv-panel-title'>Dados da empresa</h2>
          <button type='button' className='gs-text-btn' onClick={() => setEditing(true)}>Editar</button>
        </div>
        <dl className='pv-data'>
          {rows.map((row) => (
            <div key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value || <span className='gs-muted'>Não informado</span>}</dd>
            </div>
          ))}
        </dl>
      </section>
    );
  }

  return (
    <section className='pv-panel' aria-labelledby='pv-data-title'>
      <h2 id='pv-data-title' className='pv-panel-title'>Dados da empresa</h2>
      {fromOsDigital && <p className='pv-panel-hint'>As alterações também valem no cadastro da empresa no OS Digital.</p>}
      <form className='pv-form' onSubmit={handleSubmit} noValidate>
        <div className='pv-fields'>
          {FIELDS.map((field) => {
            // O e-mail recebe o link de acesso: na empresa do OS Digital so muda por la.
            const locked = fromOsDigital && field.key === 'email';
            return (
              <Field
                key={field.key}
                field={field}
                value={form[field.key]}
                onChange={change}
                invalid={invalid.includes(field.key)}
                readOnly={locked}
                hint={locked ? 'Para trocar o e-mail, altere o cadastro da empresa no OS Digital.' : null}
              />
            );
          })}
        </div>
        <h3 className='pv-form-subtitle'>Endereço</h3>
        <div className='pv-fields'>
          {ADDRESS.map((field) => (
            <Field
              key={field.key}
              field={field}
              value={form[field.key]}
              onChange={change}
              onBlur={field.key === 'zipcode' ? handleCepBlur : undefined}
              invalid={invalid.includes(field.key)}
            />
          ))}
        </div>
        <div className='pv-panel-actions'>
          <button type='button' className='gs-btn gs-btn-secondary' onClick={cancel} disabled={saving}>Cancelar</button>
          <button type='submit' className='gs-btn gs-btn-primary' disabled={saving}>{saving ? 'Salvando…' : 'Salvar dados'}</button>
        </div>
      </form>
    </section>
  );
}
